'use client';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { DELETE_ALL_NOTIFICATIONS, READ_NOTIFICATION_BY_ID } from '@/Graphql/mutation';
import { GET_ALL_NOTIFICATION_COUNT, GET_ALL_NOTIFICATIONS } from '@/Graphql/queries';
import { cn } from '@/lib/utils';
import { getRoutes } from '@/routes/routePaths';
import { Notification } from '@/Types/gql/graphql';
import { authState } from '@/vars/auth';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';

dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

function formatTime(time: string) {
  const now = dayjs();
  const diffInDays = dayjs(time).diff(now, 'day');
  if (diffInDays > 7) {
    return dayjs(time).format('MMM D, YYYY');
  } else if (diffInDays === 0) {
    const diffInHours = now.diff(time, 'hour');
    if (diffInHours === 0) {
      const diffInMinutes = now.diff(time, 'minute');
      return `${diffInMinutes}m`;
    }
    return `${diffInHours}h`;
  } else {
    return `${Math.abs(diffInDays)}d`;
  }
}

interface NotificationProps {
  isNotificationOpen: boolean;
  setIsNotificationOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const CancelledNotifications = [
  'session-cancelled-by-admin',
  'trainee-cancellation-request-received',
  'cancellation-request-received',
  'admin-cancellation-notice',
  'cancellation-request-by-trainee',
  'session-cancelled-by-admin-without-trainer',
];

const PurchaseNotifications = [
  'subscription-confirmed',
  'purchase-confirmed',
  'extra-session-added',
];

const SessionRemainder = ['setup-session-gentle-reminder'];

const Notifications: React.FC<NotificationProps> = ({
  isNotificationOpen,
  setIsNotificationOpen,
}) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { ref, inView } = useInView();
  const { userDetails } = useReactiveVar(authState);
  const [alertOpen, setAlertOpen] = useState(false);

  const { fetchMore, data, loading } = useQuery(GET_ALL_NOTIFICATIONS, {
    variables: {
      sort: { createdAt: -1 },
      limit: 10,
      offset: 0,
      filter: {
        userId: userDetails?._id,
      },
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const { data: count } = useQuery(GET_ALL_NOTIFICATION_COUNT, {
    variables: {
      filter: {
        userId: userDetails?._id,
      },
    },
  });

  const [readNotificationById] = useMutation(READ_NOTIFICATION_BY_ID);
  const [clearAllNotification, { loading: clearLoading }] = useMutation(DELETE_ALL_NOTIFICATIONS);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'reminder':
        return '⏰';
      case 'booking':
        return '📅';
      case 'trainer':
        return '👋';
      case 'cancellation':
        return '❌';
      case 'completion':
        return '✅';
      default:
        return '📌';
    }
  };

  const handleNotificationClick = (data: Notification) => {
    const id = data._id;
    const sessionId = JSON.parse(data?.eventData || '')._id;
    if (PurchaseNotifications.includes(data.eventType)) {
      navigate(getRoutes.Subscription);
    } else if (CancelledNotifications.includes(data.eventType)) {
      navigate(getRoutes.Sessions);
    } else if (SessionRemainder.includes(data.eventType)) {
      navigate(getRoutes.home);
    } else if (sessionId) {
      navigate(`/session/${sessionId}`);
    }
    if (!data.isRead) {
      readNotificationById({
        variables: { id },
        update: (cache) => {
          cache.modify({
            id: `Notification:${id}`,
            fields: {
              isRead() {
                return true;
              },
            },
          });
          const unreadCountData = cache.readQuery({
            query: GET_ALL_NOTIFICATION_COUNT,
            variables: {
              filter: {
                userId: userDetails?._id,
                isRead: false,
              },
            },
          });
          if (unreadCountData) {
            cache.writeQuery({
              query: GET_ALL_NOTIFICATION_COUNT,
              variables: {
                filter: {
                  userId: userDetails?._id,
                  isRead: false,
                },
              },
              data: {
                getAllNotificationCount: Math.max(0, unreadCountData.getAllNotificationCount - 1),
              },
            });
          }
        },
      });
    }
    setIsNotificationOpen(false);
  };

  const notifications = categorizeByDate(data?.getAllNotification as Notification[]);

  useEffect(() => {
    if (inView && !loading && count && count?.getAllNotificationCount > page * 10) {
      setPage((prev) => prev + 1);
      fetchMore({
        variables: { offset: page * 10 },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult || !fetchMoreResult.getAllNotification) {
            return prevResult;
          }
          return {
            getAllNotification: [
              ...prevResult.getAllNotification,
              ...fetchMoreResult.getAllNotification,
            ],
          };
        },
      });
    }
  }, [inView, count]);

  const handleClearAllNotification = () => {
    clearAllNotification({
      variables: {
        filter: {
          userId: userDetails?._id,
        },
      },
      refetchQueries: ['GetAllNotification', 'GetAllNotificationCount'],
    });
    setAlertOpen(false);
    setIsNotificationOpen(true);
  };

  const alertBox = () => (
    <Dialog open={alertOpen}>
      <DialogContent
        onClose={() => setAlertOpen(false)}
        className='p-7 bg-[#00001C] rounded-lg flex flex-col gap-5 border border-white'
      >
        <p className='text-lg font-semibold'>Are you sure you want to clear all notifications?</p>
        <div className='flex flex-row gap-3 justify-end'>
          <Button
            id='schedule-session-save-btn'
            variant='secondary'
            onClick={() => setAlertOpen(false)}
          >
            Cancel
          </Button>
          <Button
            id='schedule-session-save-btn'
            onClick={handleClearAllNotification}
            loading={clearLoading}
          >
            Confirm
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      <Sheet open={isNotificationOpen} onOpenChange={(open) => setIsNotificationOpen(open)}>
        <SheetContent className='w-full sm:max-w-md md:max-w-[550px] md:w-[558px] pr-0 border-0 bg-[#151525]'>
          <SheetHeader className='pb-5'>
            <SheetTitle className='text-2xl font-bold flex flex-row items-center justify-between'>
              Notifications
              {data?.getAllNotification && data?.getAllNotification?.length > 0 && (
                <Button
                  id='clear-all-notification'
                  variant='ghost'
                  size='sm'
                  className='mr-7 text-[#0094FF]'
                  onClick={() => setAlertOpen(true)}
                >
                  Clear All
                </Button>
              )}
            </SheetTitle>
          </SheetHeader>
          <ScrollArea className='h-[calc(100vh-60px)]'>
            {!loading && data?.getAllNotification.length === 0 ? (
              <div className='w-full h-[calc(100vh-120px)] flex items-center justify-center'>
                <h5>All caught up! No new notifications.</h5>
              </div>
            ) : (
              <div className='space-y-8 py-4 pr-6'>
                {Object.entries(notifications).map(
                  ([date, items]) =>
                    items.length > 0 && (
                      <div key={date} className='space-y-4'>
                        <h4 className='text-sm font-medium text-muted-foreground'>{date}</h4>
                        <div className='space-y-4'>
                          {items.map((notification: Notification) => (
                            <div
                              key={notification._id}
                              onClick={() => handleNotificationClick(notification)}
                              className='flex gap-4 rounded-lg p-4 cursor-pointer'
                            >
                              {notification.sendFromIdUser ? (
                                <Avatar>
                                  <AvatarImage
                                    src={notification.sendFromIdUser?.profileImage || ''}
                                    alt={notification.sendFromIdUser?.displayName || ''}
                                  />
                                  <AvatarFallback className='bg-bgSecondary'>
                                    {notification.sendFromIdUser.displayName?.[0]}
                                  </AvatarFallback>
                                </Avatar>
                              ) : (
                                <span className='flex h-8 w-8 items-center justify-center rounded-full bg-muted'>
                                  {getNotificationIcon(notification.eventType)}
                                </span>
                              )}
                              <div className='flex-1'>
                                <div className='flex items-center gap-1'>
                                  <h5
                                    className={cn(
                                      'font-medium',
                                      notification.isRead ? 'text-muted-foreground' : '',
                                    )}
                                  >
                                    {notification.title}
                                  </h5>
                                  <span className='text-[#A8ADB9]'>&#x2022;</span>
                                  <span className='text-sm text-muted-foreground'>
                                    {formatTime(notification.createdAt)}
                                  </span>
                                </div>
                                <p
                                  className={cn(
                                    'text-sm',
                                    notification.isRead
                                      ? 'text-muted-foreground'
                                      : 'text-[#C5C8D0]',
                                  )}
                                >
                                  {notification.message}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ),
                )}
                {loading && <NotificationSkeleton />}
                <div ref={ref} className='h-4' />
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>
      {alertBox()}
    </>
  );
};

export default Notifications;

interface CategorizedNotification {
  Today: Array<Notification>;
  Yesterday: Array<Notification>;
  Older: Array<Notification>;
}

const categorizeByDate = (items: Array<Notification>) => {
  const today = dayjs().startOf('day');
  const yesterday = today.subtract(1, 'day');
  const categorized: CategorizedNotification = {
    Today: [],
    Yesterday: [],
    Older: [],
  };
  items?.forEach((item) => {
    const createdAt = dayjs(item?.createdAt);
    if (createdAt.isSame(today, 'day')) {
      categorized.Today.push(item);
    } else if (createdAt.isSame(yesterday, 'day')) {
      categorized.Yesterday.push(item);
    } else {
      categorized.Older.push(item);
    }
  });
  return categorized;
};

const NotificationSkeleton = () => {
  return (
    <div className='space-y-4'>
      {[...Array(3)].map((_, index) => (
        <div key={index} className='flex gap-4 rounded-lg p-4'>
          <Skeleton className='h-10 w-10 rounded-full bg-bgSecondary' />{' '}
          {/* Avatar or icon skeleton */}
          <div className='flex-1 space-y-2'>
            <div className='flex items-center gap-2'>
              <Skeleton className='h-5 w-36 bg-bgSecondary' /> {/* Title skeleton */}
              <Skeleton className='h-4 w-16 bg-bgSecondary' /> {/* Time skeleton */}
            </div>
            <Skeleton className='h-4 w-full bg-bgSecondary' /> {/* Message skeleton */}
          </div>
        </div>
      ))}
    </div>
  );
};
